import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { PageHeader } from '../components/PageHeader';
import { useApprovals, useApprovalDecision } from '../hooks/useOps';
import type { ApprovalRequest, ApprovalStatus, Reversibility } from '../api/types';
import { formatDate, humanizeStatus } from '../lib/format';

const REVERSIBILITY_META: Record<
  Reversibility,
  { label: string; color: 'info' | 'warning' | 'error'; undo: string }
> = {
  reversible: { label: 'Reversible', color: 'info', undo: 'Reversible by record.' },
  comms_out: {
    label: 'Sends to client',
    color: 'warning',
    undo: 'Goes out to a client — there is a brief window before it lands.',
  },
  money_out: {
    label: 'Money / books',
    color: 'error',
    undo: 'This cannot be undone. It affects money, tax, or the books.',
  },
};

type Decision = { req: ApprovalRequest; action: 'approve' | 'reject' } | null;

export function ApprovalsPage() {
  const [status, setStatus] = useState<ApprovalStatus | 'all'>('pending');
  const { data, isLoading, error } = useApprovals(status === 'all' ? undefined : status);
  const decide = useApprovalDecision();

  const [decision, setDecision] = useState<Decision>(null);
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const openDecision = (req: ApprovalRequest, action: 'approve' | 'reject') => {
    setDecision({ req, action });
    setNote('');
    setTyped('');
    setFormError(null);
  };

  const requiresTyped = useMemo(
    () => decision?.action === 'approve' && decision.req.reversibility === 'money_out',
    [decision],
  );

  const submit = async () => {
    if (!decision) return;
    setFormError(null);
    try {
      await decide.mutateAsync({
        id: decision.req.id,
        action: decision.action,
        note: note || undefined,
      });
      setDecision(null); // close only on backend ack (D6 — no optimistic success)
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const rows = data ?? [];

  return (
    <Box>
      <PageHeader
        title="Approval inbox"
        action={
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ApprovalStatus | 'all')}
            sx={{ width: 160 }}
          >
            {['pending', 'approved', 'rejected', 'expired', 'all'].map((s) => (
              <MenuItem key={s} value={s}>
                {humanizeStatus(s)}
              </MenuItem>
            ))}
          </TextField>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}
      {!isLoading && rows.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          Nothing here. The agent hasn’t filed any {status === 'all' ? '' : status} requests.
        </Paper>
      )}

      <Stack gap={2}>
        {rows.map((req) => {
          const rev = REVERSIBILITY_META[req.reversibility];
          const pending = req.status === 'pending';
          return (
            <Paper key={req.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Box sx={{ minWidth: 0 }}>
                  {/* The fact comes first (D1). */}
                  <Typography variant="subtitle1" fontWeight={700}>
                    {req.humanSummary}
                  </Typography>
                  <Stack direction="row" gap={1} mt={0.5} mb={1} flexWrap="wrap">
                    <Chip size="small" label={humanizeStatus(req.actionType)} variant="outlined" />
                    <Chip size="small" color={rev.color} label={rev.label} />
                    <Chip
                      size="small"
                      label={humanizeStatus(req.status)}
                      color={pending ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {req.whyApprovalNeeded}. {rev.undo}
                  </Typography>
                  {/* The agent's reason is subordinate to the facts (D1). */}
                  {req.reason && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Agent’s reason: “{req.reason}”
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Filed {formatDate(req.createdAt)} by {req.agentId}
                    {req.reviewedBy ? ` · decided by ${req.reviewedBy}` : ''}
                    {req.decisionNote ? ` · ${req.decisionNote}` : ''}
                  </Typography>
                </Box>
                {pending && (
                  <Stack gap={1} sx={{ flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      color={req.reversibility === 'money_out' ? 'error' : 'primary'}
                      startIcon={<CheckIcon />}
                      onClick={() => openDecision(req, 'approve')}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<CloseIcon />}
                      onClick={() => openDecision(req, 'reject')}
                    >
                      Reject
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Dialog open={decision !== null} onClose={() => setDecision(null)} maxWidth="sm" fullWidth>
        {decision && (
          <>
            <DialogTitle>
              {decision.action === 'approve' ? 'Approve' : 'Reject'}: {decision.req.humanSummary}
            </DialogTitle>
            <DialogContent>
              {decision.action === 'approve' && (
                <DialogContentText
                  sx={{ mb: 2 }}
                  color={decision.req.reversibility === 'money_out' ? 'error' : 'text.secondary'}
                >
                  {REVERSIBILITY_META[decision.req.reversibility].undo} The action is re-validated
                  against current state before it runs.
                </DialogContentText>
              )}
              <Stack gap={2}>
                <TextField
                  label={decision.action === 'reject' ? 'Reason (required)' : 'Note (optional)'}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  multiline
                  minRows={2}
                  required={decision.action === 'reject'}
                />
                {requiresTyped && (
                  <TextField
                    label="Type CONFIRM to proceed"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                  />
                )}
                {formError && <Alert severity="error">{formError}</Alert>}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDecision(null)}>Cancel</Button>
              <Button
                variant="contained"
                color={
                  decision.action === 'reject'
                    ? 'inherit'
                    : decision.req.reversibility === 'money_out'
                      ? 'error'
                      : 'primary'
                }
                disabled={
                  decide.isPending ||
                  (decision.action === 'reject' && !note.trim()) ||
                  (requiresTyped && typed.trim() !== 'CONFIRM')
                }
                onClick={submit}
              >
                {decision.action === 'approve' ? 'Approve & execute' : 'Reject'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
